import { useState, useRef, useEffect } from "react";
import { useStore } from "zustand";
import PropTypes from "prop-types";
import {
  TextArea,
  ButtonGroup,
  Button,
  ActionButtonGroup,
  ActionButton,
  TooltipTrigger,
  Tooltip,
  Text,
  DialogTrigger,
  Popover,
  mergeStyles,
} from "@react-spectrum/s2";
import RevertIcon from "@react-spectrum/s2/icons/Revert";
import ArrowCurvedIcon from "@react-spectrum/s2/icons/ArrowCurved";
import AIMarkIcon from "@react-spectrum/s2/icons/AIMark";
import ArrowUpSendIcon from "icon:@app/assets/S2_ArrowUpSend.svg";
import StopProcessingIcon from "icon:@app/assets/S2_StopProcessing.svg";
import { EditPromptTemplateButton } from "./EditPromptTemplate";
import { useIsAdmin } from "@app/hooks/useRoles";
import { PixelLoader } from "@react-spectrum/ai/loader";

import { store } from "@app/store";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const PromptCompletions = ({ completions, isPending, onSelect }) => {
  if (!completions?.length || isPending) {
    return null;
  }

  return (
    <div
      className={style({
        boxSizing: "border-box",
        width: "full",
        maxWidth: 800,
        paddingX: 12,
      })}
    >
      <ButtonGroup
        variant="secondary"
        size="S"
        UNSAFE_className={style({
          flexWrap: "wrap",
          flexDirection: "row",
        })}
      >
        {completions.map((completion, index) => (
          <Button
            key={index}
            aria-label={`Use completion: ${completion}`}
            onPress={() => onSelect(completion)}
          >
            <ArrowCurvedIcon />
            <Text>{completion}</Text>
          </Button>
        ))}
      </ButtonGroup>
    </div>
  );
};

PromptCompletions.propTypes = {
  completions: PropTypes.arrayOf(PropTypes.string),
  isPending: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

const PromptEditor = ({
  inputRef,
  prompt,
  setPrompt,
  placeholder,
  isPending,
  onKeyDown,
  onSubmit,
  onReset,
  onRedo,
  videoId,
  getPromptTemplate,
  templateKey,
  actions,
  styles,
}) => {
  const isAdmin = useIsAdmin();

  return (
    <div
      className={mergeStyles(
        style({
          display: "flex",
          flexDirection: "column",
          alignItems: "end",
        }),
        styles,
      )}
    >
      <div
        className={style({
          display: "flex",
          flexDirection: "column",
          gap: 4,
          width: "full",
        })}
      >
        <TextArea
          ref={inputRef}
          aria-label="Prompt"
          placeholder={placeholder}
          prefix={<PixelLoader isPlaying={isPending} />}
          value={prompt}
          isDisabled={isPending}
          onChange={setPrompt}
          onKeyDown={onKeyDown}
          UNSAFE_className="__PromptInput __PromptInput--quiet"
        />
      </div>
      <div
        className={style({
          display: "flex",
          alignItems: "center",
          gap: 12,
        })}
      >
        <ActionButtonGroup size="S" isQuiet>
          {isAdmin && !!onRedo && (
            <TooltipTrigger>
              <ActionButton onPress={onRedo} aria-label="Generate">
                <RevertIcon />
              </ActionButton>
              <Tooltip>Regenerate</Tooltip>
            </TooltipTrigger>
          )}
          {isAdmin && (
            <TooltipTrigger>
              <EditPromptTemplateButton
                videoId={videoId}
                getPromptTemplate={getPromptTemplate}
                templateKey={templateKey}
              />
              <Tooltip>Edit prompt template</Tooltip>
            </TooltipTrigger>
          )}
          {!!actions && actions}
        </ActionButtonGroup>
        <TooltipTrigger>
          <Button
            aria-label={isPending ? "Cancel" : "Submit prompt"}
            variant="primary"
            isDisabled={!prompt}
            onPress={() =>
              isPending ? onReset?.() : onSubmit(prompt, setPrompt)
            }
          >
            {isPending ? <StopProcessingIcon /> : <ArrowUpSendIcon />}
          </Button>
          <Tooltip>{isPending ? "Cancel" : "Submit prompt"}</Tooltip>
        </TooltipTrigger>
      </div>
    </div>
  );
};

PromptEditor.propTypes = {
  inputRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  prompt: PropTypes.string.isRequired,
  setPrompt: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  isPending: PropTypes.bool,
  onKeyDown: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  onRedo: PropTypes.func,
  videoId: PropTypes.string.isRequired,
  getPromptTemplate: PropTypes.func,
  templateKey: PropTypes.string,
  actions: PropTypes.node,
  styles: PropTypes.string,
};

export const EditPrompt = ({
  videoId,
  placeholder,
  completions,
  getPromptTemplate,
  templateKey,
  isPending,
  onSubmit,
  onReset,
  onRedo,
  actions = null,
  styles = "",
}) => {
  const [prompt, setPrompt] = useState("");
  const inputRef = useRef(null);

  const promptHint = useStore(store, (state) => state.promptHint);
  const setPromptHint = useStore(store, (state) => state.setPromptHint);
  const isMobile = useStore(store, (state) => state.isMobile);

  useEffect(() => {
    if (promptHint && !prompt.includes(promptHint)) {
      setPrompt((prev) => `${prev}${!!prev ? "\n" : ""}${promptHint}`);
      setPromptHint(null);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }, [promptHint, prompt]);

  const handlePromptKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (prompt.trim()) {
        onSubmit(prompt, setPrompt);
      }
    }
  };

  if (isMobile) {
    return (
      <div
        className={style({
          alignSelf: "start",
          padding: 12,
          backgroundColor: "elevated",
          borderRadius: "full",
          boxShadow: "elevated",
        })}
      >
        <DialogTrigger>
          <Button variant="genai" aria-label="Open prompt">
            <AIMarkIcon />
          </Button>

          <Popover position="bottom" hideArrow offset={-64}>
            <div
              className={style({
                boxSizing: "border-box",
                height: "full",
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
                gap: 12,
                // padding: 12,
                paddingTop: 12,
              })}
            >
              <PromptCompletions
                completions={completions}
                isPending={isPending}
                onSelect={setPrompt}
              />
              <PromptEditor
                inputRef={inputRef}
                prompt={prompt}
                setPrompt={setPrompt}
                placeholder={placeholder}
                isPending={isPending}
                onKeyDown={handlePromptKeyDown}
                onSubmit={onSubmit}
                onReset={onReset}
                onRedo={onRedo}
                videoId={videoId}
                getPromptTemplate={getPromptTemplate}
                templateKey={templateKey}
                actions={actions}
                styles={mergeStyles(
                  styles,
                  // style({ backgroundColor: "transparent", boxShadow: "none" }),
                )}
              />
            </div>
          </Popover>
        </DialogTrigger>
      </div>
    );
  }

  return (
    <>
      <PromptCompletions
        completions={completions}
        isPending={isPending}
        onSelect={setPrompt}
      />
      <PromptEditor
        inputRef={inputRef}
        prompt={prompt}
        setPrompt={setPrompt}
        placeholder={placeholder}
        isPending={isPending}
        onKeyDown={handlePromptKeyDown}
        onSubmit={onSubmit}
        onReset={onReset}
        onRedo={onRedo}
        videoId={videoId}
        getPromptTemplate={getPromptTemplate}
        templateKey={templateKey}
        actions={actions}
        styles={styles}
      />
    </>
  );
};

EditPrompt.propTypes = {
  videoId: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  completions: PropTypes.arrayOf(PropTypes.string),
  getPromptTemplate: PropTypes.func,
  templateKey: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onReset: PropTypes.func,
  onRedo: PropTypes.func,
  isPending: PropTypes.bool,
  actions: PropTypes.node,
  styles: PropTypes.string,
};
